/**
 * Mounts the MCP bus.
 *
 * **Mounted from `App.js`, never from the settings modal.** An external client
 * can call a tool at any moment, and the modal is open for a few seconds a
 * month; a bus that lived there would answer nothing the rest of the time and
 * the failure would look like "Apiboo is still starting" to the agent.
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { startMcpBus } from '../mcp/bus';
import { isTauri } from '../utils/tauri';
import { recordFeature, REQUEST, ACTION } from '../services/telemetry';

// An external agent made a successful tool call (usage key request.mcp, card
// c8d24044). Counted here, not in src/mcp: the MCP graph stays telemetry-free.
const countToolCall = (_tool, ok) => {
  if (ok) recordFeature(REQUEST.MCP);
};

// The MCP server is switched on (a state, not a click): read once per start.
// The effect re-runs on a language change; the daily mark dedupes that.
async function countServerOn() {
  try {
    const { invoke } = await import('@tauri-apps/api/core');
    const s = await invoke('mcp_settings_get');
    if (s?.enabled) recordFeature(ACTION.MCP_SERVER_ON);
  } catch {
    // counting must never break the bus
  }
}

export function useMcpBus() {
  const { t } = useTranslation();
  useEffect(() => {
    if (!isTauri()) return undefined;
    // The folder name is read per REQUEST inside the bus rather than captured
    // once, so a name change is picked up by the next tool call. (The effect
    // itself does re-run on a language change — `t` changes identity — so this
    // is not what avoids a resubscribe; it is what makes the value correct
    // between resubscribes.)
    const sub = startMcpBus(() => ({ folderName: t('mcp.memoryFolderName') }), countToolCall);
    countServerOn();
    return () => sub.unlisten();
  }, [t]);
}

export default useMcpBus;
